import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { insuranceApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { InsurancePolicy } from "@/features/worksheets/types";

interface InsuranceFormValues {
  company: string;
  policyNo: string;
  cashValue: string;
  loanBalance: string;
}

const validationSchema = Yup.object({
  company: Yup.string().required("Company is required"),
});

function toInitialValues(record: InsurancePolicy | null): InsuranceFormValues {
  return {
    company: record?.company ?? "",
    policyNo: record?.policyNo ?? "",
    cashValue: record ? centsToDollars(record.cashValue) : "",
    loanBalance: record ? centsToDollars(record.loanBalance) : "",
  };
}

function toPayload(v: InsuranceFormValues): Record<string, unknown> {
  return {
    company: v.company,
    policyNo: v.policyNo,
    cashValue: dollarsToCents(v.cashValue),
    loanBalance: dollarsToCents(v.loanBalance),
  };
}

export function InsuranceDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: InsurancePolicy | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: InsuranceFormValues) {
    const payload = toPayload(values);
    if (record) {
      await insuranceApi.update(caseId, record.id, payload);
    } else {
      await insuranceApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Policy"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField
          name="company"
          label="Company"
          labelClassName={FIELD_LABEL}
          placeholder="e.g. Prudential"
        />
        <FormField name="policyNo" label="Policy No." labelClassName={FIELD_LABEL} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="cashValue" label="Cash Value" />
        <MoneyField name="loanBalance" label="Loan Balance" />
      </div>
    </CrudDialog>
  );
}
